import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatWidget from './components/ChatWidget';

// ── Replace this URL after deploying the CEDS Dashboard to Vercel ──────────
const CEDS_URL = 'https://padd-cdbg-hub-jae1.vercel.app';

// ── Business services — shown as a standalone callout above the resource library
const BUSINESS_SERVICES = [
  {
    icon: '🏢',
    title: 'Business Succession Planning',
    desc: 'Free, confidential support for Panhandle business owners planning their next chapter. Complete a short readiness questionnaire and a PADD advisor will follow up within 5 business days.',
    cta: 'Get started — create a free account',
    action: { type: 'internal', path: '/login?mode=signup' },
  },
  {
    icon: '📋',
    title: 'CEDS Community Survey Results',
    desc: 'See what Panhandle residents and business owners said about economic priorities, housing, childcare, and quality of life in the 2026 regional survey.',
    cta: 'View survey results →',
    action: { type: 'external', url: CEDS_URL + '?tab=survey' },
  },
];

const C = {
  navy: '#122326', slate: '#213A3E', gold: '#BF6847',
  goldLight: '#DBDDAC', cream: '#F0EFEB', warm: '#F7F6F3',
  sand: '#E2E0DB', white: '#FFFFFF',
};

// action.type: 'internal' (useNavigate) | 'external' (anchor tag)
const resources = [
  // ── Interactive Tools ────────────────────────────────────────────────────
  {
    icon: '📊', title: 'CEDS Dashboard', category: 'Tools',
    desc: '2026–2031 Comprehensive Economic Development Strategy — goals, data dashboards, and regional action plan for all 11 Panhandle counties.',
    action: { type: 'external', url: CEDS_URL },
    tool: true,
  },
  {
    icon: '🎯', title: 'Funding Finder', category: 'Tools',
    desc: 'Answer a few questions and get matched with grants your community is competitive for. Takes 5–7 minutes.',
    action: { type: 'internal', path: '/funding-finder' },
    tool: true,
  },
  {
    icon: '📚', title: 'CDBG Knowledge Base', category: 'Tools',
    desc: 'Searchable FAQ covering national objectives, application process, compliance, and more — 54 questions across 9 topics.',
    action: { type: 'internal', path: '/knowledge-base' },
    tool: true,
  },
  // ── Nebraska DED ─────────────────────────────────────────────────────────
  {
    icon: '🏛️', title: 'Nebraska CDBG Program', category: 'Nebraska DED',
    desc: 'Nebraska DED program page with current funding rounds, application materials, and state-specific guidance.',
    action: { type: 'external', url: 'https://opportunity.nebraska.gov/programs/community/cdbg/' },
  },
  {
    icon: '✅', title: 'CDBG Compliance Checklist', category: 'Nebraska DED',
    desc: 'Required exhibits and documentation checklist for managing a Nebraska CDBG grant from award through closeout.',
    action: { type: 'external', url: 'https://opportunity.nebraska.gov/programs/community/cdbg/' },
  },
  // ── Compliance ───────────────────────────────────────────────────────────
  {
    icon: '🌿', title: 'Environmental Review (24 CFR 58)', category: 'Compliance',
    desc: 'Federal environmental review regulation covering RROF, ERR requirements, and the Assumption of Responsibility process.',
    action: { type: 'external', url: 'https://www.ecfr.gov/current/title-24/subtitle-A/part-58' },
  },
  {
    icon: '💵', title: 'Davis-Bacon Wage Determinations', category: 'Compliance',
    desc: 'Search current prevailing wage requirements by county for all CDBG construction and rehabilitation contracts.',
    action: { type: 'external', url: 'https://sam.gov/content/wage-determinations' },
  },
  {
    icon: '⚖️', title: '2 CFR Part 200 — Uniform Guidance', category: 'Compliance',
    desc: 'Federal cost principles and administrative requirements that govern all CDBG financial management and procurement.',
    action: { type: 'external', url: 'https://www.ecfr.gov/current/title-2/subtitle-A/chapter-II/part-200' },
  },
  // ── Data & Financial ─────────────────────────────────────────────────────
  {
    icon: '📊', title: 'HUD Low-Mod Income Summary Data', category: 'Data & Financial',
    desc: 'Census-based LMI data used to document area benefit national objective qualification for CDBG projects.',
    action: { type: 'external', url: 'https://www.hudexchange.info/programs/acs-low-mod-summary-data/' },
  },
  {
    icon: '📈', title: 'HUD Income Limits', category: 'Data & Financial',
    desc: 'Annual AMI-based income limit tables by county and family size — used to determine LMI household eligibility.',
    action: { type: 'external', url: 'https://www.huduser.gov/portal/datasets/il.html' },
  },
  {
    icon: '💻', title: 'HUD Exchange — CDBG Hub', category: 'Data & Financial',
    desc: 'Central HUD resource for CDBG guidance documents, training modules, waivers, and program Q&As.',
    action: { type: 'external', url: 'https://www.hudexchange.info/programs/cdbg/' },
  },
];

const faqs = [
  { q: 'What is CDBG and who can apply?', a: 'The Community Development Block Grant (CDBG) program is a federal program administered by Nebraska DED. Any non-entitlement city, village, or county in the Panhandle can apply. PADD helps communities prepare and submit applications at no additional cost to dues-paying members.' },
  { q: 'Does my project need to meet a "national objective"?', a: 'Yes — every CDBG-funded activity must benefit low-to-moderate income (LMI) persons, eliminate slum or blight, or address an urgent community need. The most common approach is demonstrating that at least 51% of residents in the project service area are LMI, using HUD census data.' },
  { q: 'Is there a local match requirement?', a: 'Nebraska CDBG does not have a mandatory match requirement for most programs. However, showing local investment can strengthen a competitive application — and some program types (like economic development) do require private leverage.' },
  { q: 'What does my municipality get for its PADD dues?', a: 'Dues-paying members receive grant writing assistance, application management, compliance support, and access to the full resource library and Knowledge Base. Non-members can still use the Funding Finder and public resources, but project assistance requires membership.' },
];

export default function CDBGHub() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const categories = ['All', ...new Set(resources.map((r) => r.category))];
  const filtered = activeFilter === 'All' ? resources : resources.filter((r) => r.category === activeFilter);

  const styles = {
    page: { minHeight: '100vh', backgroundColor: C.warm, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
    nav: { backgroundColor: C.navy, padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    logo: { fontFamily: "'Georgia', serif", fontSize: '20px', fontWeight: 700, color: C.cream },
    loginBtn: { backgroundColor: C.gold, color: C.white, border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
    hero: { backgroundColor: C.navy, padding: '64px 32px', textAlign: 'center' },
    heroTitle: { fontFamily: "'Georgia', serif", fontSize: '42px', fontWeight: 700, color: C.cream, maxWidth: '700px', margin: '0 auto 16px', lineHeight: 1.2 },
    heroSub: { fontSize: '18px', color: C.sand, maxWidth: '560px', margin: '0 auto 32px', lineHeight: 1.6 },
    heroBtn: { backgroundColor: C.gold, color: C.white, border: 'none', padding: '14px 32px', borderRadius: '10px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' },
    section: { maxWidth: '1000px', margin: '0 auto', padding: '48px 24px' },
    sectionTitle: { fontFamily: "'Georgia', serif", fontSize: '28px', fontWeight: 700, color: C.navy, marginBottom: '8px', textAlign: 'center' },
    sectionSub: { fontSize: '15px', color: C.slate, textAlign: 'center', marginBottom: '32px' },
    filterRow: { display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px' },
    filterBtn: (active) => ({ padding: '6px 16px', borderRadius: '20px', border: `1px solid ${active ? C.gold : C.sand}`, backgroundColor: active ? C.gold : C.white, color: active ? C.white : C.slate, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }),
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' },
    card: { backgroundColor: C.white, borderRadius: '12px', padding: '24px', border: `1px solid ${C.sand}`, transition: 'box-shadow 0.2s', cursor: 'default' },
    cardIcon: { fontSize: '28px', marginBottom: '12px' },
    cardTitle: { fontSize: '16px', fontWeight: 700, color: C.navy, marginBottom: '8px' },
    cardDesc: { fontSize: '14px', color: C.slate, lineHeight: 1.5 },
    cardCat: { display: 'inline-block', padding: '3px 10px', backgroundColor: C.cream, borderRadius: '12px', fontSize: '11px', fontWeight: 600, color: C.slate, marginTop: '12px' },
    faqItem: { backgroundColor: C.white, borderRadius: '10px', border: `1px solid ${C.sand}`, marginBottom: '12px', overflow: 'hidden' },
    faqQ: { padding: '16px 20px', fontSize: '15px', fontWeight: 600, color: C.navy, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', width: '100%', textAlign: 'left' },
    faqA: (open) => ({ maxHeight: open ? '200px' : '0px', overflow: 'hidden', transition: 'max-height 0.3s ease', padding: open ? '0 20px 16px' : '0 20px', fontSize: '14px', color: C.slate, lineHeight: 1.6 }),
    footer: { backgroundColor: C.navy, padding: '32px', textAlign: 'center', color: C.sand, fontSize: '14px' },
  };

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <span style={styles.logo}>PADD CDBG Hub</span>
        <button style={styles.loginBtn} onClick={() => navigate('/login')}>Log In</button>
      </nav>

      <section style={styles.hero}>
        <h1 style={styles.heroTitle}>Your Gateway to Community Development Resources</h1>
        <p style={styles.heroSub}>The Panhandle Area Development District helps Nebraska Panhandle communities access grants, plan projects, and build stronger futures.</p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button style={styles.heroBtn} onClick={() => navigate('/login')}>Access Your Dashboard</button>
          <button
            style={{ ...styles.heroBtn, backgroundColor: 'transparent', border: `2px solid ${C.gold}`, color: C.cream }}
            onClick={() => navigate('/funding-finder')}
          >
            Find Funding for Your Project
          </button>
        </div>
      </section>

      {/* ── Business Services callout ── */}
      <section style={{ backgroundColor: C.cream, padding: '40px 24px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ ...styles.sectionTitle, textAlign: 'left', marginBottom: 6 }}>Business Services</h2>
          <p style={{ ...styles.sectionSub, textAlign: 'left', marginBottom: 20 }}>
            Free programs for Panhandle business owners.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
            {BUSINESS_SERVICES.map((svc) => (
              <div
                key={svc.title}
                onClick={() => svc.action.type === 'internal' ? navigate(svc.action.path) : window.open(svc.action.url, '_blank', 'noopener,noreferrer')}
                style={{
                  backgroundColor: C.white, borderRadius: 12, padding: '24px',
                  border: `1px solid ${C.sand}`, borderTop: `3px solid ${C.gold}`,
                  cursor: 'pointer', transition: 'box-shadow 0.2s',
                  display: 'flex', flexDirection: 'column', gap: 12,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)')}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
              >
                <div style={{ fontSize: 32 }}>{svc.icon}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>{svc.title}</div>
                <p style={{ fontSize: 14, color: C.slate, lineHeight: 1.6, margin: 0 }}>{svc.desc}</p>
                <div style={{
                  marginTop: 'auto', paddingTop: 8,
                  fontSize: 13, fontWeight: 700, color: C.gold,
                }}>
                  {svc.cta} →
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Resource Library</h2>
        <p style={styles.sectionSub}>Guides, templates, and tools to support your community development projects.</p>
        <div style={styles.filterRow}>
          {categories.map((cat) => (
            <button key={cat} style={styles.filterBtn(activeFilter === cat)} onClick={() => setActiveFilter(cat)}>{cat}</button>
          ))}
        </div>
        <div style={styles.grid}>
          {filtered.map((r, i) => {
            const cardStyle = {
              ...styles.card,
              cursor: 'pointer',
              borderTop: r.tool ? `3px solid ${C.gold}` : `1px solid ${C.sand}`,
              borderLeft: r.tool ? `1px solid ${C.sand}` : undefined,
              borderRight: r.tool ? `1px solid ${C.sand}` : undefined,
              borderBottom: r.tool ? `1px solid ${C.sand}` : undefined,
            };
            const inner = (
              <>
                <div style={styles.cardIcon}>{r.icon}</div>
                <div style={styles.cardTitle}>{r.title}</div>
                <div style={styles.cardDesc}>{r.desc}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                  <span style={styles.cardCat}>{r.category}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: r.tool ? C.gold : C.slate }}>
                    {r.tool ? 'Open Tool →' : 'View →'}
                  </span>
                </div>
              </>
            );
            if (r.action.type === 'internal') {
              return (
                <div key={i} style={cardStyle}
                  onClick={() => navigate(r.action.path)}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'; e.currentTarget.style.borderTopColor = C.gold; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderTopColor = r.tool ? C.gold : C.sand; }}
                >
                  {inner}
                </div>
              );
            }
            return (
              <a key={i} href={r.action.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <div style={cardStyle}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = C.sand; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {inner}
                </div>
              </a>
            );
          })}
        </div>
      </section>

      <section style={{ ...styles.section, backgroundColor: C.cream, maxWidth: 'none', padding: '48px 24px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={styles.sectionTitle}>Frequently Asked Questions</h2>
          <p style={{ ...styles.sectionSub, marginBottom: '24px' }}>Common questions about PADD services and CDBG programs.</p>
          {faqs.map((faq, i) => (
            <div key={i} style={styles.faqItem}>
              <button style={styles.faqQ} onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}>
                <span>{faq.q}</span>
                <span style={{ fontSize: '18px', color: C.gold }}>{expandedFaq === i ? '−' : '+'}</span>
              </button>
              <div style={styles.faqA(expandedFaq === i)}>{faq.a}</div>
            </div>
          ))}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button
              onClick={() => navigate('/knowledge-base')}
              style={{ background: 'none', border: `1.5px solid ${C.gold}`, color: C.gold, padding: '10px 28px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              See all 54 FAQs in the Knowledge Base →
            </button>
          </div>
        </div>
      </section>

      <footer style={styles.footer}>
        <p style={{ margin: 0 }}>Panhandle Area Development District &middot; Serving 11 counties across the Nebraska Panhandle</p>
      </footer>

      <ChatWidget />
    </div>
  );
}
